import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { $createLinkNode, AutoLinkNode, LinkNode } from "@lexical/link";
import { $isListItemNode, ListItemNode, ListNode } from "@lexical/list";
import { TRANSFORMERS } from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  type ElementNode,
  INDENT_CONTENT_COMMAND,
  KEY_ENTER_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  type TextNode,
} from "lexical";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { attachmentServiceClient } from "@/connect";
import { NODE_TYPE_INVESTMENT } from "@/constants/lexical";
import {
  convertContentSchemaToLexical as convertFromContentSchema,
  convertLexicalToContentSchema as convertToContentSchema,
} from "@/lib/content-schema-converter";
import { cn } from "@/lib/utils";
import type { Attachment } from "@/types/proto/api/v1/attachment_service_pb";
import { ActionItemNode } from "./nodes/ActionItemNode";
import { useBaseAppNodeDeletion } from "./nodes/BaseAppNode";
import { $createChecklistNode, ChecklistNode } from "./nodes/ChecklistNode";
import type { DecisionData } from "./nodes/DecisionNode";
import { $createDecisionNode, DecisionNode } from "./nodes/DecisionNode";
import { EditableImageNode } from "./nodes/EditableImageNode";
import { EditableLinkNode } from "./nodes/EditableLinkNode";
import { $createImageNode, ImageNode } from "./nodes/ImageNode";
import { PriceTrendNode } from "./nodes/PriceTrendNode";
import type { SelfContractData } from "./nodes/SelfContractNode";
import { $createSelfContractNode, SelfContractNode } from "./nodes/SelfContractNode";
import { $createStockNode, StockNode } from "./nodes/StockNode";
import { $createTagNode, TagNode } from "./nodes/TagNode";
import { ImageEditPlugin } from "./plugins/ImageEditPlugin";
import { ImagePastePlugin } from "./plugins/ImagePastePlugin";
import { InputTransformPlugin } from "./plugins/InputTransformPlugin";
import { LexicalSuggestionsPlugin } from "./plugins/LexicalSuggestionsPlugin";
import { LinkEditPlugin } from "./plugins/LinkEditPlugin";
import { PasteTransformPlugin } from "./plugins/PasteTransformPlugin";
import { SlashCommandPlugin } from "./plugins/SlashCommandPlugin";

// 主题配置
const theme = {
  ltr: "ltr",
  rtl: "rtl",
  paragraph: "editor-paragraph",
  quote: "editor-quote",
  heading: {
    h1: "editor-heading-h1",
    h2: "editor-heading-h2",
    h3: "editor-heading-h3",
    h4: "editor-heading-h4",
    h5: "editor-heading-h5",
    h6: "editor-heading-h6",
  },
  list: {
    nested: {
      listitem: "editor-nested-listitem",
    },
    ol: "editor-list-ol",
    ul: "editor-list-ul",
    listitem: "editor-listitem",
    listitemChecked: "editor-listitem-checked",
    listitemUnchecked: "editor-listitem-unchecked",
  },
  image: "editor-image",
  link: "editor-link",
  text: {
    bold: "editor-text-bold",
    code: "editor-text-code",
    italic: "editor-text-italic",
    strikethrough: "editor-text-strikethrough",
    subscript: "editor-text-subscript",
    superscript: "editor-text-superscript",
    underlineb: "editor-text-underline",
    underline: "editor-text-underline",
  },
  code: "editor-code",
  codeHighlight: {
    atrule: "editor-token-attr",
    attr: "editor-token-attr",
    boolean: "editor-token-property",
    builtin: "editor-token-selector",
    cdata: "editor-token-comment",
    char: "editor-token-selector",
    class: "editor-token-function",
    "class-name": "editor-token-function",
    comment: "editor-token-comment",
    constant: "editor-token-property",
    deleted: "editor-token-deleted",
    doctype: "editor-token-comment",
    entity: "editor-token-operator",
    function: "editor-token-function",
    important: "editor-token-variable",
    inserted: "editor-token-inserted",
    keyword: "editor-token-atrule",
    namespace: "editor-token-variable",
    number: "editor-token-property",
    operator: "editor-token-operator",
    prolog: "editor-token-comment",
    property: "editor-token-property",
    punctuation: "editor-token-punctuation",
    regex: "editor-token-variable",
    selector: "editor-token-selector",
    string: "editor-token-selector",
    symbol: "editor-token-property",
    tag: "editor-token-atrule",
    url: "editor-token-operator",
    variable: "editor-token-variable",
  },
};

// 节点映射：注册自定义节点
function onError(error: Error) {
  console.error(error);
}

export interface LexicalEditorProps {
  className?: string;
  initialContent?: string;
  placeholder?: string;
  onContentChange?: (content: string, jsonContent: unknown) => void;
  onCreateAttachment?: (attachment: Attachment) => Promise<Attachment>;
  isFocusMode?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  editorInstanceId?: string;
}

export interface LexicalEditorRefActions {
  getEditor: () => unknown;
  getContent: () => string;
  getJSON: () => unknown;
  setContent: (content: string) => void;
  reset: () => void;
  insertStock: (symbol: string, sentiment?: "bullish" | "bearish" | "neutral") => void;
  insertTag: (name: string) => void;
  insertSelfContract: (initialData?: Partial<SelfContractData>) => void;
  insertDecision: (initialData?: Partial<DecisionData>) => void;
  insertChecklist: () => void;
  focus: () => void;
  recalculateHeight: () => void;
}

// 内部组件，使用 Lexical composer context
function LexicalEditorInner({
  initialContent,
  placeholder,
  onContentChange,
  onCreateAttachment,
  isFocusMode,
  onFocus,
  onBlur,
  forwardedRef,
  editorInstanceId,
}: {
  initialContent?: string;
  placeholder: string;
  onContentChange?: (content: string, jsonContent: unknown) => void;
  onCreateAttachment?: (attachment: Attachment & { content: Uint8Array }) => Promise<Attachment>;
  isFocusMode?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  forwardedRef: React.ForwardedRef<LexicalEditorRefActions>;
  editorInstanceId?: string;
}) {
  const [editor] = useLexicalComposerContext();

  // Register BaseAppNode deletion commands (Backspace/Delete for custom nodes)
  useBaseAppNodeDeletion();

  // 使用 Connect RPC 客户端创建 attachment
  const defaultOnCreateAttachment = async (attachment: Attachment): Promise<Attachment> => {
    const result = await attachmentServiceClient.createAttachment({ attachment });
    return result;
  };

  // 使用用户提供的函数或默认函数
  const handleCreateAttachment = onCreateAttachment || defaultOnCreateAttachment;
  const loadedContentRef = useRef<string>("");
  const prevContentRef = useRef<string>("");
  const [editorHeight, setEditorHeight] = useState<number>(120); // 默认高度 120px（约5行）
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const setEditorHeightWithLog = useCallback(
    (height: number, _source: string) => {
      setEditorHeight(height);
    },
    [editorHeight],
  );

  // 初始化编辑器内容
  // 场景1：新建 memo - initialContent 始终为空，不执行任何操作
  // 场景2：编辑 memo - initialContent 从空变为有内容，执行初始化
  // 场景3：用户输入 - initialContent 从一个内容变为另一个内容，不重新初始化
  // 场景4：保存后清空 - initialContent 从有内容变为空，清空编辑器
  // 场景5：重新加载同一个编辑器来编辑不同 memo
  useEffect(() => {
    if (!editor) return;

    const prevContent = prevContentRef.current;
    const currentContent = initialContent || "";

    // 场景4：从有内容变为空（保存后重置），清空编辑器并重置高度
    if (prevContent !== "" && currentContent === "") {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        root.append(paragraph);
        paragraph.select();
      });
      loadedContentRef.current = "";
      prevContentRef.current = "";
      // Reset editor height to default after clearing
      setEditorHeightWithLog(120, "clear-editor");
      return;
    }

    // 如果已经有加载过的内容，且当前内容不为空
    // 检查内容是否真的相同（防止误判为用户编辑）
    if (loadedContentRef.current !== "" && currentContent !== "") {
      // 如果内容完全相同，说明是重复触发，跳过
      if (loadedContentRef.current === currentContent) {
        return;
      }
      // 如果内容不同但都非空，这可能意味着：
      // 1. 用户正在编辑（内容变化）
      // 2. 编辑器被重用来编辑另一个 memo（新内容加载到已有内容的编辑器）
      // 检查 prevContent，如果为空，说明是从清空状态重新加载
      const isNewLoad = prevContent === "" && currentContent !== loadedContentRef.current;
      if (!isNewLoad) {
        // 用户在编辑，跳过初始化
        prevContentRef.current = currentContent;
        return;
      }
      // 是重新加载新内容，清空旧的 loadedContentRef 以允许重新初始化
      loadedContentRef.current = "";
    }

    // Only load content when not previously loaded, and initialContent is not empty
    if (currentContent.trim() !== "") {
      try {
        // Convert Content Schema to Lexical if necessary
        const lexicalJSON = convertFromContentSchema(currentContent);

        const parsedState = JSON.parse(lexicalJSON);
        editor.setEditorState(editor.parseEditorState(JSON.stringify(parsedState)));
        loadedContentRef.current = currentContent;
        prevContentRef.current = currentContent;
      } catch (e) {
        console.error("[LexicalEditor] Failed to parse initial content:", e);
        // Parse failed but still record as loaded to avoid retry
        loadedContentRef.current = currentContent;
        prevContentRef.current = currentContent;
      }
    }

    // 依赖 editor 和 initialContent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, initialContent]);

  // 暴露编辑器方法给父组件 - 只暴露必要的方法，避免过度更新
  useImperativeHandle(
    forwardedRef,
    () => ({
      getEditor: () => editor,
      getContent: () => {
        try {
          // Clean up editable nodes before serialization
          editor.update(() => {
            const root = $getRoot();
            const visited = new Set<string>();

            // Helper function to recursively clean editable nodes
            const cleanEditableNodes = (node: any) => {
              if (!node || visited.has(node.getKey())) return;
              visited.add(node.getKey());

              if (node instanceof EditableLinkNode) {
                // Convert editable link back to regular link
                const url = node.__url;
                const text = node.__text || url;

                // Create link node with text
                const linkNode = $createLinkNode(url, { target: "_blank", rel: "noopener noreferrer" });
                const textNode = $createTextNode(text);
                linkNode.append(textNode);

                // Keep the current selection if possible, otherwise just replace
                node.replace(linkNode);
                return; // Don't traverse children after replacement
              } else if (node instanceof EditableImageNode) {
                // Convert editable image back to regular image
                const imageNode = $createImageNode(node.__src, node.__altText, node.__width, node.__height);
                node.replace(imageNode);
                return; // Don't traverse children after replacement
              }

              // Recursively clean children
              if (node.getChildren) {
                const children = node.getChildren();
                for (const child of children) {
                  cleanEditableNodes(child);
                }
              }
            };

            // Start cleaning from root
            const children = root.getChildren();
            for (const child of children) {
              cleanEditableNodes(child);
            }
          });

          // Get Lexical JSON
          const json = editor.getEditorState().toJSON();
          const lexicalJSONString = JSON.stringify(json, null, 2);

          // Convert to Content Schema (for backend storage)
          const contentSchemaJSON = convertToContentSchema(lexicalJSONString);
          return contentSchemaJSON;
        } catch (e) {
          console.error("[LexicalEditor] Failed to get content:", e);
          return "";
        }
      },
      getJSON: () => {
        try {
          return editor.getEditorState().toJSON();
        } catch (e) {
          console.error("[LexicalEditor] Failed to get JSON:", e);
          return null;
        }
      },
      setContent: (content: string) => {
        try {
          // Convert Content Schema to Lexical if necessary
          const lexicalJSON = convertFromContentSchema(content);

          // Parse the content and update the editor state
          const parsedState = JSON.parse(lexicalJSON);
          const editorState = editor.parseEditorState(JSON.stringify(parsedState));
          editor.setEditorState(editorState);
        } catch (e) {
          console.error("[LexicalEditor] Failed to parse content:", e);
        }
      },
      insertStock: (symbol, sentiment = "neutral") => {
        editor.update(() => {
          const stockNode = $createStockNode(symbol, sentiment, null);
          $insertNodes([stockNode]);
        });
        // 插入后添加段落节点，让用户可以继续输入
        editor.update(() => {
          const paragraphNode = $createParagraphNode();
          $insertNodes([paragraphNode]);
          paragraphNode.select(0, 0);
        });
      },
      insertTag: (name) => {
        editor.update(() => {
          const tagNode = $createTagNode(name);
          $insertNodes([tagNode]);
        });
        editor.update(() => {
          const paragraphNode = $createParagraphNode();
          $insertNodes([paragraphNode]);
          paragraphNode.select(0, 0);
        });
      },
      insertSelfContract: (initialData?: Partial<SelfContractData>) => {
        editor.update(() => {
          const contractNode = $createSelfContractNode(initialData);
          $insertNodes([contractNode]);
        });
        // 插入后添加段落节点，让用户可以继续输入
        editor.update(() => {
          const paragraphNode = $createParagraphNode();
          $insertNodes([paragraphNode]);
          paragraphNode.select(0, 0);
        });

        // 触发高度重新计算（等待 DOM 更新完成）
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // 手动触发一次高度计算
            editor.getEditorState().read(() => {
              const root = $getRoot();
              const children = root.getChildren();
              let estimatedHeight = 0;
              let hasContentText = false;

              for (const child of children) {
                const nodeType = child.getType();
                const textContent = child.getTextContent();

                if (nodeType === NODE_TYPE_INVESTMENT.SELF_CONTRACT) {
                  estimatedHeight += 700;
                  hasContentText = true;
                } else if (nodeType === NODE_TYPE_INVESTMENT.DECISION) {
                  estimatedHeight += 650;
                  hasContentText = true;
                } else if (nodeType === "action-item") {
                  estimatedHeight += 250;
                  hasContentText = true;
                } else if (nodeType === "checklist") {
                  estimatedHeight += 150 + textContent.split("\n").length * 30;
                  hasContentText = true;
                } else if (nodeType === "stock") {
                  estimatedHeight += 40;
                  hasContentText = true;
                } else if (nodeType === "tag") {
                  estimatedHeight += 30;
                  hasContentText = true;
                } else if (nodeType === "image") {
                  estimatedHeight += 150;
                  hasContentText = true;
                } else if (nodeType === "heading") {
                  estimatedHeight += 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "code") {
                  estimatedHeight += Math.max(60, textContent.split("\n").length * 18);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "quote") {
                  estimatedHeight += Math.max(40, textContent.split("\n").length * 20);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else {
                  const lineCount = Math.max(1, textContent.split("\n").length);
                  estimatedHeight += lineCount * 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                }
              }

              if (!hasContentText) {
                estimatedHeight = 0;
              }

              const DEFAULT_HEIGHT = 120;
              const newHeight = Math.min(2000, Math.max(DEFAULT_HEIGHT, estimatedHeight));
              setEditorHeightWithLog(newHeight, "insert-self-contract-trigger");
            });
          });
        });
      },
      insertDecision: (initialData?: Partial<DecisionData>) => {
        editor.update(() => {
          const decisionNode = $createDecisionNode(initialData);
          $insertNodes([decisionNode]);
        });
        // 插入后添加段落节点，让用户可以继续输入
        editor.update(() => {
          const paragraphNode = $createParagraphNode();
          $insertNodes([paragraphNode]);
          paragraphNode.select(0, 0);
        });

        // 触发高度重新计算（等待 DOM 更新完成）
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // 手动触发一次高度计算
            editor.getEditorState().read(() => {
              const root = $getRoot();
              const children = root.getChildren();
              let estimatedHeight = 0;
              let hasContentText = false;

              for (const child of children) {
                const nodeType = child.getType();
                const textContent = child.getTextContent();

                if (nodeType === NODE_TYPE_INVESTMENT.SELF_CONTRACT) {
                  estimatedHeight += 700;
                  hasContentText = true;
                } else if (nodeType === NODE_TYPE_INVESTMENT.DECISION) {
                  estimatedHeight += 650;
                  hasContentText = true;
                } else if (nodeType === "action-item") {
                  estimatedHeight += 250;
                  hasContentText = true;
                } else if (nodeType === "checklist") {
                  estimatedHeight += 150 + textContent.split("\n").length * 30;
                  hasContentText = true;
                } else if (nodeType === "stock") {
                  estimatedHeight += 40;
                  hasContentText = true;
                } else if (nodeType === "tag") {
                  estimatedHeight += 30;
                  hasContentText = true;
                } else if (nodeType === "image") {
                  estimatedHeight += 150;
                  hasContentText = true;
                } else if (nodeType === "heading") {
                  estimatedHeight += 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "code") {
                  estimatedHeight += Math.max(60, textContent.split("\n").length * 18);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "quote") {
                  estimatedHeight += Math.max(40, textContent.split("\n").length * 20);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else {
                  const lineCount = Math.max(1, textContent.split("\n").length);
                  estimatedHeight += lineCount * 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                }
              }

              if (!hasContentText) {
                estimatedHeight = 0;
              }

              const DEFAULT_HEIGHT = 120;
              const newHeight = Math.min(2000, Math.max(DEFAULT_HEIGHT, estimatedHeight));
              setEditorHeightWithLog(newHeight, "insert-decision-trigger");
            });
          });
        });
      },
      insertChecklist: () => {
        editor.update(() => {
          const checklistNode = $createChecklistNode();
          $insertNodes([checklistNode]);
        });
        // 插入后添加段落节点，让用户可以继续输入
        editor.update(() => {
          const paragraphNode = $createParagraphNode();
          $insertNodes([paragraphNode]);
          paragraphNode.select(0, 0);
        });

        // 触发高度重新计算（等待 DOM 更新完成）
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // 手动触发一次高度计算
            editor.getEditorState().read(() => {
              const root = $getRoot();
              const children = root.getChildren();
              let estimatedHeight = 0;
              let hasContentText = false;

              for (const child of children) {
                const nodeType = child.getType();
                const textContent = child.getTextContent();

                if (nodeType === NODE_TYPE_INVESTMENT.SELF_CONTRACT) {
                  estimatedHeight += 700;
                  hasContentText = true;
                } else if (nodeType === NODE_TYPE_INVESTMENT.DECISION) {
                  estimatedHeight += 650;
                  hasContentText = true;
                } else if (nodeType === "action-item") {
                  estimatedHeight += 250;
                  hasContentText = true;
                } else if (nodeType === "checklist") {
                  estimatedHeight += 150 + textContent.split("\n").length * 30;
                  hasContentText = true;
                } else if (nodeType === "stock") {
                  estimatedHeight += 40;
                  hasContentText = true;
                } else if (nodeType === "tag") {
                  estimatedHeight += 30;
                  hasContentText = true;
                } else if (nodeType === "image") {
                  estimatedHeight += 150;
                  hasContentText = true;
                } else if (nodeType === "heading") {
                  estimatedHeight += 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "code") {
                  estimatedHeight += Math.max(60, textContent.split("\n").length * 18);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "quote") {
                  estimatedHeight += Math.max(40, textContent.split("\n").length * 20);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else {
                  const lineCount = Math.max(1, textContent.split("\n").length);
                  estimatedHeight += lineCount * 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                }
              }

              if (!hasContentText) {
                estimatedHeight = 0;
              }

              const DEFAULT_HEIGHT = 120;
              const newHeight = Math.min(2000, Math.max(DEFAULT_HEIGHT, estimatedHeight));
              setEditorHeightWithLog(newHeight, "insert-checklist-trigger");
            });
          });
        });
      },
      focus: () => {
        editor.focus();
      },
      recalculateHeight: () => {
        // Trigger a height recalculation using requestAnimationFrame
        // to ensure DOM is updated before calculating
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Read the current editor state and calculate height
            editor.getEditorState().read(() => {
              const root = $getRoot();
              const children = root.getChildren();
              let estimatedHeight = 0;
              let hasContentText = false;

              // 遍历所有节点计算高度
              for (const child of children) {
                const nodeType = child.getType();
                const textContent = child.getTextContent();

                // Block nodes have their own height
                if (nodeType === NODE_TYPE_INVESTMENT.SELF_CONTRACT) {
                  estimatedHeight += 700; // SelfContractNode 估计高度
                  hasContentText = true;
                } else if (nodeType === NODE_TYPE_INVESTMENT.DECISION) {
                  estimatedHeight += 650; // DecisionNode 估计高度
                  hasContentText = true;
                } else if (nodeType === "action-item") {
                  estimatedHeight += 250; // ActionItemNode 估计高度
                  hasContentText = true;
                } else if (nodeType === "checklist") {
                  estimatedHeight += 150 + textContent.split("\n").length * 30;
                  hasContentText = true;
                } else if (nodeType === "stock") {
                  estimatedHeight += 40;
                  hasContentText = true;
                } else if (nodeType === "tag") {
                  estimatedHeight += 30;
                  hasContentText = true;
                } else if (nodeType === "image") {
                  estimatedHeight += 150;
                  hasContentText = true;
                } else if (nodeType === "heading") {
                  estimatedHeight += 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "code") {
                  estimatedHeight += Math.max(60, textContent.split("\n").length * 18);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else if (nodeType === "quote") {
                  estimatedHeight += Math.max(40, textContent.split("\n").length * 20);
                  hasContentText = hasContentText || textContent.trim() !== "";
                } else {
                  const lineCount = Math.max(1, textContent.split("\n").length);
                  estimatedHeight += lineCount * 24;
                  hasContentText = hasContentText || textContent.trim() !== "";
                }
              }

              // 如果没有任何有意义的内容
              if (!hasContentText) {
                estimatedHeight = 0;
              }

              // 最小高度 120px（5行），最大 2000px
              const DEFAULT_HEIGHT = 120;
              const newHeight = Math.min(2000, Math.max(DEFAULT_HEIGHT, estimatedHeight));
              setEditorHeightWithLog(newHeight, "recalculate-height-manual");
            });
          });
        });
      },
      reset: () => {
        // Clear the editor content and reset internal refs
        // This ensures the editor is fully reset after saving
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          const paragraph = $createParagraphNode();
          root.append(paragraph);
          paragraph.select();
        });
        // Reset the loaded content refs so next initialization works correctly
        loadedContentRef.current = "";
        prevContentRef.current = "";
        // Reset editor height to default
        setEditorHeightWithLog(120, "reset-editor");
      },
    }),
    [editor, setEditorHeightWithLog],
  );

  // 使用 ref 存储 onContentChange 避免重复注册监听器导致失焦
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;

  // Track whether this is the initial mount to properly reset height
  const isInitialMountRef = useRef(true);
  const hasReceivedFirstContentUpdateRef = useRef(false);

  // Reset height when component first mounts
  // This ensures height is reset to default (120px) for a fresh editor instance
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      // Always reset to default height on initial mount
      // The update listener will then adjust based on content
      setEditorHeightWithLog(120, "initial-mount");

      // Also reset any inline styles on the contentEditable element
      // This ensures DOM state is fully reset on remount
      if (contentEditableRef.current) {
        contentEditableRef.current.style.minHeight = "";
        contentEditableRef.current.style.maxHeight = "";
      }
    }
  }, [editorInstanceId, setEditorHeightWithLog]);

  // 监听编辑器容器高度变化（包含 DecoratorNode）
  // 这确保当 DecisionNode/SelfContractNode 的 panel 展开/收起时，编辑器高度能够自适应
  useEffect(() => {
    if (!containerRef.current) return;

    let rafId: number | null = null;
    let lastHeight = 0;
    let initialized = false;
    const decoratorResizeObservers: ResizeObserver[] = [];

    const calculateHeight = () => {
      const container = containerRef.current;
      if (!container) return;

      // Find all DecoratorNodes in the document
      const allDecoratorNodes = document.querySelectorAll('[data-lexical-decorator="true"]');

      // Filter decorators that belong to this editor by checking if they're visually inside/near our container
      let maxDecoratorBottom = 0;
      const containerRect = container.getBoundingClientRect();

      allDecoratorNodes.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // Check if this decorator is near our container (overlap in X axis)
        const isInOurEditor = Math.abs(rect.left - containerRect.left) < 1000;

        if (isInOurEditor) {
          const relativeBottom = rect.bottom - containerRect.top;

          if (relativeBottom > maxDecoratorBottom && relativeBottom < 10000) {
            // Sanity check
            maxDecoratorBottom = relativeBottom;
          }
        }
      });

      const scrollHeight = container.scrollHeight;
      const decoratorHeight = maxDecoratorBottom > 0 ? maxDecoratorBottom + 24 : 0;

      // For decorators (Decision/SelfContract nodes), use decorator height as the source of truth
      // because scrollHeight doesn't update when panels collapse
      let actualHeight;
      if (allDecoratorNodes.length > 0) {
        // Use decorator height, not scrollHeight!
        actualHeight = decoratorHeight;
      } else {
        // No decorators, use scrollHeight
        actualHeight = scrollHeight;
      }

      // Skip initial render
      if (!initialized) {
        lastHeight = actualHeight;
        initialized = true;
        return;
      }

      const heightDelta = actualHeight - lastHeight;

      // Check for significant height change (> 50px)
      const significantChange = Math.abs(heightDelta) > 50;

      // Update if there's a significant change
      if (significantChange) {
        lastHeight = actualHeight;

        // Update editor height
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          setEditorHeight(Math.max(120, actualHeight));
          rafId = null;
        });
      }
    };

    // Observe container for resize
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          requestAnimationFrame(calculateHeight);
          break;
        }
      }
    });
    resizeObserver.observe(containerRef.current);

    // Observe all DecoratorNodes for resize
    const updateDecoratorObservers = () => {
      // Clean up old observers
      decoratorResizeObservers.forEach((ro) => ro.disconnect());
      decoratorResizeObservers.length = 0;

      // Find all DecoratorNodes
      const allDecoratorNodes = document.querySelectorAll('[data-lexical-decorator="true"]');
      const containerRect = containerRef.current?.getBoundingClientRect();

      allDecoratorNodes.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const isInOurEditor = containerRect && Math.abs(rect.left - containerRect.left) < 1000;

        if (isInOurEditor) {
          const ro = new ResizeObserver(() => requestAnimationFrame(calculateHeight));
          ro.observe(el);
          decoratorResizeObservers.push(ro);
        }
      });
    };

    // Initial setup
    updateDecoratorObservers();

    // Update observers on structural changes
    const mutationObserver = new MutationObserver((mutations) => {
      const hasNodeMutation = mutations.some((m) => m.type === "childList");
      if (hasNodeMutation) {
        updateDecoratorObservers();
        requestAnimationFrame(calculateHeight);
      }
    });

    mutationObserver.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    // Initial calculation after a short delay
    setTimeout(() => requestAnimationFrame(calculateHeight), 100);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      decoratorResizeObservers.forEach((ro) => ro.disconnect());
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // 监听内容变化 - 只注册一次，避免重新渲染导致失焦
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      try {
        // Mark that we've received at least one update
        if (!hasReceivedFirstContentUpdateRef.current) {
          hasReceivedFirstContentUpdateRef.current = true;
        }

        // 使用 editorState.toJSON() 获取 JSON
        const json = editorState.toJSON();
        const lexicalJSONString = JSON.stringify(json, null, 2);

        // Convert to Content Schema for state storage ✅ CRITICAL FIX
        const contentSchemaJSON = convertToContentSchema(lexicalJSONString);
        onContentChangeRef.current?.(contentSchemaJSON, json);

        // 动态调整编辑器高度 - 使用传入的 editorState 而不是 editor.getEditorState()
        // 这确保我们读取的是更新后的状态
        editorState.read(() => {
          const root = $getRoot();
          const children = root.getChildren();
          let estimatedHeight = 0;

          // 检测内容是否为空（只有空段落）
          let isEmpty = true;
          let hasContentText = false;

          // 遍历所有节点计算高度
          for (const child of children) {
            const nodeType = child.getType();
            const textContent = child.getTextContent();

            // Block nodes have their own height
            if (nodeType === NODE_TYPE_INVESTMENT.SELF_CONTRACT) {
              estimatedHeight += 700; // SelfContractNode 估计高度 (约650px + margin + padding)
              isEmpty = false;
              hasContentText = true;
            } else if (nodeType === NODE_TYPE_INVESTMENT.DECISION) {
              estimatedHeight += 650; // DecisionNode 估计高度
              isEmpty = false;
              hasContentText = true;
            } else if (nodeType === "action-item") {
              estimatedHeight += 250; // ActionItemNode 估计高度
              isEmpty = false;
              hasContentText = true;
            } else if (nodeType === "checklist") {
              estimatedHeight += 150 + textContent.split("\n").length * 30; // ChecklistNode 基础高度 + 每项高度
              isEmpty = false;
              hasContentText = true;
            } else if (nodeType === "stock") {
              estimatedHeight += 40; // StockNode 高度
              isEmpty = false;
              hasContentText = true;
            } else if (nodeType === "tag") {
              estimatedHeight += 30; // TagNode 高度
              isEmpty = false;
              hasContentText = true;
            } else if (nodeType === "image") {
              estimatedHeight += 150; // ImageNode 估计高度
              isEmpty = false;
              hasContentText = true;
            } else if (nodeType === "heading") {
              estimatedHeight += 24; // HeadingNode 高度
              hasContentText = hasContentText || textContent.trim() !== "";
            } else if (nodeType === "code") {
              estimatedHeight += Math.max(60, textContent.split("\n").length * 18);
              hasContentText = hasContentText || textContent.trim() !== "";
            } else if (nodeType === "quote") {
              estimatedHeight += Math.max(40, textContent.split("\n").length * 20);
              hasContentText = hasContentText || textContent.trim() !== "";
            } else {
              // 默认段落/文本节点
              const lineCount = Math.max(1, textContent.split("\n").length);
              estimatedHeight += lineCount * 24;
              hasContentText = hasContentText || textContent.trim() !== "";
            }
          }

          // 如果没有任何有意义的内容（只有空段落），将 estimatedHeight 设为 0
          if (!hasContentText) {
            estimatedHeight = 0;
            isEmpty = true;
          }

          // 最小高度 120px（5行），最大 2000px（支持大型 block nodes 如 SelfContractNode）
          // 如果内容为空，使用默认 120px
          const DEFAULT_HEIGHT = 120;
          const newHeight = Math.min(2000, Math.max(DEFAULT_HEIGHT, estimatedHeight));

          // 如果内容为空（只有空段落），直接使用默认高度，跳过 scrollHeight 校验
          // 这避免了读取到旧的 scrollHeight 导致高度无法恢复的问题
          if (isEmpty) {
            setEditorHeightWithLog(DEFAULT_HEIGHT, "content-update-empty-default");
            return;
          }

          // 确保 contentEditable 实际高度同步
          // 注意：DecoratorNodes 的动态高度变化由 ResizeObserver 处理
          if (containerRef.current) {
            // 对于有 DecoratorNodes 的情况，ResizeObserver 会处理动态高度变化
            // 这里只需要处理没有动态节点的情况
            const hasDynamicNodes = children.some(
              (c) => c.getType() === NODE_TYPE_INVESTMENT.SELF_CONTRACT || c.getType() === NODE_TYPE_INVESTMENT.DECISION,
            );

            if (!hasDynamicNodes) {
              // 没有动态节点，使用计算高度
              setEditorHeightWithLog(newHeight, "content-update-no-dynamic");
            }
          } else {
            // 没有 scrollHeight 引用时，仅使用计算高度
            setEditorHeightWithLog(newHeight, "content-update-calculated");
          }
        });
      } catch (e) {
        console.error("[LexicalEditor] Update listener error:", e);
      }
    });
  }, [editor]);

  // Handle image click to prevent opening in new window
  const handleContentEditableClick = useCallback((e: React.MouseEvent) => {
    const targetEl = e.target as HTMLElement;
    if (targetEl.tagName === "IMG") {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Handle Tab key for list indentation
  const handleContentEditableKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "Tab") {
        return;
      }

      // Check if we're in a list item
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        const focusNode = selection.focus.getNode();

        // Find list item node by traversing up
        let listItem: ListItemNode | null = null;
        let node: ElementNode | TextNode | null = focusNode;
        let depth = 0;
        while (node && depth < 10) {
          if ($isListItemNode(node)) {
            listItem = node;
            break;
          }
          node = node.getParent();
          depth++;
        }

        // Only handle Tab if we're in a list item
        if (listItem) {
          e.preventDefault();
          e.stopPropagation();

          // Dispatch indent or outdent command
          if (e.shiftKey) {
            editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
          }
        }
      });
    },
    [editor],
  );

  // Prevent Cmd/Ctrl+Enter from creating new list items
  // This ensures the save shortcut works correctly in lists
  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent) => {
        // Check if Cmd/Ctrl+Enter is pressed
        if (event.metaKey || event.ctrlKey) {
          // Prevent default behavior (creating new list item)
          // and let the global handler trigger save
          event.preventDefault();
          return true; // Stop Lexical from processing this command
        }
        return false; // Let Lexical handle normal Enter
      },
      1, // Low priority to run after Lexical's internal handlers
    );
  }, [editor]);

  // Helper function to calculate list depth (outside useEffect)

  return (
    <div ref={containerRef} className={cn("editor-container w-full relative", isFocusMode ? "flex-1" : "")}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className={cn("editor-input w-full p-2 outline-none prose prose-sm max-w-none cursor-text", "focus:outline-none")}
            style={{ minHeight: `${editorHeight}px` }}
            ref={contentEditableRef}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={handleContentEditableClick}
            onKeyDown={handleContentEditableKeyDown}
          />
        }
        placeholder={
          <div className="editor-placeholder absolute top-2 left-2 pointer-events-none text-muted-foreground/60 text-sm select-none">
            {placeholder}
          </div>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <HistoryPlugin />
      <LinkPlugin />
      <LinkEditPlugin />
      <ListPlugin />
      <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
      <InputTransformPlugin />
      <PasteTransformPlugin />
      <ImagePastePlugin onCreateAttachment={handleCreateAttachment} />
      <ImageEditPlugin />
      <SlashCommandPlugin />
      <LexicalSuggestionsPlugin />
    </div>
  );
}

const LexicalEditorWrapper = forwardRef<LexicalEditorRefActions, LexicalEditorProps>(
  (
    {
      className,
      initialContent = "",
      placeholder = "输入 / 以输入命令...",
      onContentChange,
      isFocusMode = false,
      onFocus,
      onBlur,
      editorInstanceId,
    },
    ref,
  ) => {
    const initialConfig = {
      namespace: `MemoEditor-${editorInstanceId || "default"}`,
      theme,
      onError,
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        LinkNode,
        AutoLinkNode,
        StockNode,
        TagNode,
        ImageNode,
        ChecklistNode,
        PriceTrendNode,
        ActionItemNode,
        SelfContractNode,
        DecisionNode,
        EditableLinkNode,
        EditableImageNode,
      ],
    };

    return (
      <div className={cn("flex flex-col justify-start items-start relative w-full bg-inherit", className)}>
        <LexicalComposer initialConfig={initialConfig}>
          <LexicalEditorInner
            initialContent={initialContent}
            placeholder={placeholder}
            onContentChange={onContentChange}
            isFocusMode={isFocusMode}
            onFocus={onFocus}
            onBlur={onBlur}
            forwardedRef={ref}
            editorInstanceId={editorInstanceId}
          />
        </LexicalComposer>
      </div>
    );
  },
);

LexicalEditorWrapper.displayName = "LexicalEditorWrapper";

export default LexicalEditorWrapper;
